import { useEffect, useState } from "react";
import { Box, Chip, IconButton, TextField, Typography } from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useQueryClient } from "@tanstack/react-query";
import { formatUSPhone } from "src/utils";
import { useCompanyNumberMutation } from "../hooks/useCompanyNumberMutation";

const activeChipSx = {
  height: 22,
  borderRadius: "999px",
  bgcolor: "rgba(34,197,94,0.12)",
  border: "1px solid rgba(34,197,94,0.18)",
  color: "#15803d",
  fontSize: 10,
  fontWeight: 800,
};

const pendingPortingChipSx = {
  height: 22,
  borderRadius: "999px",
  bgcolor: "rgba(251,142,0,0.12)",
  border: "1px solid rgba(251,142,0,0.22)",
  color: "#b45309",
  fontSize: 10,
  fontWeight: 800,
};

const pendingBuyingChipSx = {
  height: 22,
  borderRadius: "999px",
  bgcolor: "rgba(59,130,246,0.10)",
  border: "1px solid rgba(59,130,246,0.18)",
  color: "#1d4ed8",
  fontSize: 10,
  fontWeight: 800,
};

const inactiveChipSx = {
  height: 22,
  borderRadius: "999px",
  bgcolor: "rgba(107,114,128,0.10)",
  border: "1px solid rgba(107,114,128,0.18)",
  color: "var(--fx-surface-textsecondary, #64748B)",
  fontSize: 10,
  fontWeight: 800,
};

type AssignedNumberBlockProps = {
  companyNumberId: string;
  phone: string;
  name?: string | null;
  status: string;
  source: string;
};

function getStatusChip(status: string, source: string) {
  if (status === "ACTIVE") {
    return { label: "Active", sx: activeChipSx };
  }

  if (status === "PENDING" && source === "PORTING") {
    return { label: "Pending porting", sx: pendingPortingChipSx };
  }

  if (status === "PENDING" && source === "BUYING") {
    return { label: "Pending activation", sx: pendingBuyingChipSx };
  }

  return { label: status, sx: inactiveChipSx };
}

export function AssignedNumberBlock({
  companyNumberId,
  phone,
  name,
  status,
  source,
}: AssignedNumberBlockProps) {
  const { updateNumber } = useCompanyNumberMutation();
  const queryClient = useQueryClient();

  const hasRealName = Boolean(name?.trim());
  const dbName = hasRealName ? name!.trim() : "My number";
  const statusChip = getStatusChip(status, source);
  const canEditName = status === "ACTIVE";

  const [isEditingName, setIsEditingName] = useState(false);
  const [numberName, setNumberName] = useState(dbName);
  const [draftName, setDraftName] = useState(dbName);
  const [isSavingName, setIsSavingName] = useState(false);

  useEffect(() => {
    setNumberName(dbName);

    if (!isEditingName) {
      setDraftName(dbName);
    }
  }, [dbName, isEditingName]);

  const handleStartEdit = () => {
    if (!canEditName) return;
    setDraftName(hasRealName ? numberName : "");
    setIsEditingName(true);
  };

  const handleCancelEdit = () => {
    setDraftName(numberName);
    setIsEditingName(false);
  };

  const handleSaveName = async () => {
    const cleanName = draftName.trim();
    if (!cleanName) return;

    try {
      setIsSavingName(true);

      await updateNumber({
        id: companyNumberId,
        phone,
        name: cleanName,
      });

      setNumberName(cleanName);
      setDraftName(cleanName);
      setIsEditingName(false);

      await queryClient.invalidateQueries({
        queryKey: ["company-numbers"],
        exact: false,
      });
    } catch (error) {
      console.error("Error updating number name:", error);
    } finally {
      setIsSavingName(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
        justifyContent: "flex-end",
      }}
    >
      {isEditingName ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <TextField
            size="small"
            value={draftName}
            autoFocus
            disabled={isSavingName}
            placeholder="My number"
            onChange={(event) => setDraftName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleSaveName();
              if (event.key === "Escape") handleCancelEdit();
            }}
            sx={{
              width: 140,
              "& .MuiInputBase-root": {
                height: 30,
                borderRadius: 1.5,
                fontSize: 12,
                fontWeight: 700,
              },
            }}
          />
          <IconButton size="small" onClick={handleSaveName} disabled={isSavingName} sx={{ color: "#15803d" }}>
            <CheckRoundedIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton size="small" onClick={handleCancelEdit} disabled={isSavingName} sx={{ color: "var(--fx-surface-textsecondary, #64748B)" }}>
            <CloseRoundedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.35 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: hasRealName ? 700 : 600,
              color: hasRealName ? "var(--fx-surface-textsecondary, #64748B)" : "rgba(107,114,128,0.55)",
              fontStyle: hasRealName ? "normal" : "italic",
              maxWidth: 100,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {numberName}
          </Typography>
          {canEditName ? (
            <IconButton size="small" onClick={handleStartEdit} sx={{ width: 22, height: 22, color: "var(--fx-surface-texttertiary, #94A3B8)" }}>
              <EditRoundedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          ) : null}
        </Box>
      )}

      <Typography sx={{ fontSize: 14, fontWeight: 800, color: "var(--fx-surface-text, #0F172A)", lineHeight: 1.2 }}>
        {formatUSPhone(phone)}
      </Typography>

      <Chip size="small" label={`NUMBER: ${statusChip.label.toUpperCase()}`} sx={statusChip.sx} />
    </Box>
  );
}
